import React from "react";

const COLORS = { 
  screening: "#1CA069", 
  adoption: "#072D4B", 
  text: "#3D4A52", 
  border: "#dfe8e6", 
  muted: "#9aa5a3" 
};

function TrendChart({ title, data }) {
  const normalizedData = (data || []).map((d) => ({
    period: String(d.period || d.month || d.year || ""),
    screening: Number(d.screening ?? d.unique_farmers_screened ?? 0),
    adoption: Number(d.adoption ?? d.unique_farmers_adopted ?? 0),
  }));

  if (!normalizedData || normalizedData.length === 0) {
    return (
      <div style={{ background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 20 }}>
        <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: COLORS.muted }}>No data available.</div>
      </div>
    );
  }

  const width = 900;
  const height = 260;
  const padding = { top: 20, right: 30, bottom: 55, left: 55 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const totalItems = normalizedData.length;
  const maxVal = Math.max(...normalizedData.flatMap((d) => [d.screening, d.adoption])) * 1.1 || 1;
  const stepX = totalItems > 1 ? innerW / (totalItems - 1) : innerW / 2;

  const pointsFor = (key) =>
    normalizedData.map((d, i) => {
      const x = padding.left + (totalItems === 1 ? innerW / 2 : i * stepX);
      const y = padding.top + innerH - (d[key] / maxVal) * innerH;
      return [x, y];
    });

  const toPath = (pts) => pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");

  const screeningPts = pointsFor("screening");
  const adoptionPts = pointsFor("adoption");

  const yTicks = 4;
  const gridLines = Array.from({ length: yTicks + 1 }).map((_, i) => {
    const y = padding.top + (innerH / yTicks) * i;
    const val = Math.round(maxVal - (maxVal / yTicks) * i);
    return { y, val };
  });

  const tickInterval = totalItems > 12 ? Math.ceil(totalItems / 10) : 1;
  const shouldRotate = totalItems > 8;

  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 20 }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 14 }}>{title}</div>
        <div style={{ display: "flex", gap: 14, fontSize: 12, fontWeight: 600 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, color: COLORS.screening }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS.screening }} /> Screening
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 5, color: COLORS.adoption }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS.adoption }} /> Adoption
          </span>
        </div>
      </div>

      {/* SVG Chart */}
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", overflow: "visible" }}>
        {/* Y-Axis Gridlines & Labels */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={padding.left} x2={width - padding.right} y1={g.y} y2={g.y} stroke={COLORS.border} strokeWidth="1" />
            <text x={padding.left - 10} y={g.y + 4} textAnchor="end" fontSize="10.5" fill={COLORS.muted}>
              {g.val.toLocaleString()}
            </text>
          </g>
        ))}

        {/* Lines */}
        <path d={toPath(screeningPts)} fill="none" stroke={COLORS.screening} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={toPath(adoptionPts)} fill="none" stroke={COLORS.adoption} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots */}
        {screeningPts.map((p, i) => (
          <circle key={"s" + i} cx={p[0]} cy={p[1]} r="3.5" fill="#fff" stroke={COLORS.screening} strokeWidth="2">
            <title>{`Screening: ${normalizedData[i].screening.toLocaleString()}`}</title>
          </circle>
        ))}
        {adoptionPts.map((p, i) => (
          <circle key={"a" + i} cx={p[0]} cy={p[1]} r="3.5" fill="#fff" stroke={COLORS.adoption} strokeWidth="2">
            <title>{`Adoption: ${normalizedData[i].adoption.toLocaleString()}`}</title>
          </circle>
        ))}

        {/* X-Axis Labels */}
        {normalizedData.map((d, i) => {
          // Render label every Nth item or if it's the last item
          const showLabel = i % tickInterval === 0 || i === totalItems - 1;
          if (!showLabel) return null;

          const x = padding.left + (totalItems === 1 ? innerW / 2 : i * stepX);
          const y = height - padding.bottom + 18;

          return (
            <text
              key={i}
              x={x}
              y={y}
              textAnchor={shouldRotate ? "end" : "middle"}
              transform={shouldRotate ? `rotate(-35, ${x}, ${y})` : undefined}
              fontSize="10"
              fill={COLORS.muted}
            >
              {d.period}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export default TrendChart;